import { useReactQuery } from '@flik/shared/api';
import { useMemo } from 'react';

export interface Session {
  _type: string;
  created_by?: string | null;
  created_on?: string | null;
  id: string;
  last_updated_by?: string | null;
  last_updated_on?: string | null;
  livekit_room_id: string;
  livekit_room_name: string;
  livekit_url: string;
  max_participants: number;
  state: 'active' | 'inactive';
  title: string;
}

export interface ListSessionsResponse {
  _type: string;
  next?: string | null;
  sessions: Session[];
}

export function useSessions() {
  const apiClient = useReactQuery();

  // Fetch active sessions
  const activeSessionsQuery = apiClient.useQuery('get', '/v1/sessions', {
    params: {
      query: {
        status: 'active' as const,
        limit: 10,
      },
    },
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  // Fetch inactive sessions
  const inactiveSessionsQuery = apiClient.useQuery('get', '/v1/sessions', {
    params: {
      query: {
        status: 'inactive' as const,
        limit: 10,
      },
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const activeSessions = useMemo(() => {
    return activeSessionsQuery.data?.sessions || [];
  }, [activeSessionsQuery.data]);

  const inactiveSessions = useMemo(() => {
    return inactiveSessionsQuery.data?.sessions || [];
  }, [inactiveSessionsQuery.data]);

  const isLoading =
    activeSessionsQuery.isLoading || inactiveSessionsQuery.isLoading;
  const isError = activeSessionsQuery.isError || inactiveSessionsQuery.isError;
  const error = activeSessionsQuery.error || inactiveSessionsQuery.error;

  return {
    activeSessions,
    inactiveSessions,
    isLoading,
    isError,
    error,
    refetch: () => {
      activeSessionsQuery.refetch();
      inactiveSessionsQuery.refetch();
    },
  };
}
